import { useGamepads } from "./useGamepads";

export default function GamepadTester() {
  const list = useGamepads();

  return (
    <div style={styles.page}>
      <style>{css}</style>
      <div style={styles.wrap}>
        <header style={styles.head}>
          <div style={styles.headMark}>◈</div>
          <div>
            <h1 style={styles.title}>Gamepad Bench</h1>
            <p style={styles.sub}>
              Live input diagnostic · Gamepad API
            </p>
          </div>
          <div style={styles.count}>
            <span style={styles.countNum}>{list.length}</span>
            <span style={styles.countLbl}>
              {list.length === 1 ? "device" : "devices"}
            </span>
          </div>
        </header>

        {list.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyPulse} />
            <p style={styles.emptyTitle}>Waiting for a controller</p>
            <p style={styles.emptyHint}>
              Connect a gamepad and press any button to wake it up.
            </p>
          </div>
        ) : (
          list.map((gp) => <Controller key={gp.index} gp={gp} />)
        )}
      </div>
    </div>
  );
}

function Controller({ gp }) {
  const { sticks, lone } = pairAxes(gp.axes);
  const { grid, triggers } = splitButtons(gp.buttons);
  return (
    <section style={styles.card}>
      <div style={styles.cardHead}>
        <span style={styles.slot}>#{gp.index}</span>
        <span style={styles.gpId} title={gp.id}>
          {gp.id}
        </span>
        {gp.mapping ? (
          <span style={styles.mapping}>{gp.mapping}</span>
        ) : null}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Buttons</div>
        <div style={styles.buttons}>
          {grid.map(({ index, button }) => (
            <Button key={index} index={index} button={button} />
          ))}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Triggers</div>
        <div style={styles.axes}>
          {triggers.map(({ index, button }) => (
            <TriggerBar key={index} index={index} value={button.value} />
          ))}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Axes</div>
        <div style={styles.axes}>
          {sticks.map((s, i) => (
            <StickPad key={`stick-${i}`} label={stickLabel(i)} x={s.x} y={s.y} />
          ))}
          {lone.map(({ index, value }) => (
            <Axis key={index} index={index} value={value} />
          ))}
        </div>
      </div>
    </section>
  );
}

// Standard mapping puts the analog triggers (LT/RT) at buttons 6/7 — pull
// them out of the digital button grid and show them as 0..1 bars instead.
const TRIGGER_INDICES = new Set([6, 7]);

function splitButtons(buttons) {
  const grid = [];
  const triggers = [];
  buttons.forEach((button, index) => {
    (TRIGGER_INDICES.has(index) ? triggers : grid).push({ index, button });
  });
  return { grid, triggers };
}

// Standard mapping reports axes in x/y pairs per stick (0/1 = left, 2/3 =
// right), so group them for the circular pad; anything left over (e.g. a
// non-standard trigger-as-axis) falls back to the linear bar.
function pairAxes(axes) {
  const sticks = [];
  let i = 0;
  for (; i + 1 < axes.length; i += 2) {
    sticks.push({ x: axes[i], y: axes[i + 1] });
  }
  const lone = axes.slice(i).map((value, k) => ({ value, index: i + k }));
  return { sticks, lone };
}

function stickLabel(i) {
  if (i === 0) return "left stick";
  if (i === 1) return "right stick";
  return `stick ${i}`;
}

function StickPad({ label, x, y }) {
  // Clamp magnitude to 1 so diagonal extremes (both axes near ±1) can't
  // push the dot past the edge of the circle.
  const mag = Math.min(Math.hypot(x, y), 1);
  const angle = Math.atan2(y, x);
  const dx = ((Math.cos(angle) * mag + 1) / 2) * 100;
  const dy = ((Math.sin(angle) * mag + 1) / 2) * 100;

  return (
    <div className="gp-stick">
      <div className="gp-stick-row">
        <span className="gp-axis-name">{label}</span>
        <span className="gp-axis-val">
          {x.toFixed(2)}, {y.toFixed(2)}
        </span>
      </div>
      <div className="gp-stick-circle">
        <div className="gp-stick-cross-h" />
        <div className="gp-stick-cross-v" />
        <div className="gp-stick-dot" style={{ left: dx + "%", top: dy + "%" }} />
      </div>
    </div>
  );
}

function Button({ index, button }) {
  const { pressed, touched, value } = button;
  const pct = Math.round(value * 100);
  const cls =
    "gp-btn" +
    (pressed ? " is-pressed" : "") +
    (touched && !pressed ? " is-touched" : "");
  return (
    <div className={cls} title={`button ${index}: ${value.toFixed(3)}`}>
      {/* Fill height tracks analog value, echoing the original backgroundSize trick */}
      <span className="gp-btn-fill" style={{ height: pct + "%" }} />
      <span className="gp-btn-num">{index}</span>
    </div>
  );
}

function TriggerBar({ index, value }) {
  // Triggers report 0..1 (released..fully pressed), so the thumb maps
  // straight onto the track with no center point.
  const pos = value * 100;
  return (
    <div className="gp-axis">
      <div className="gp-axis-row">
        <span className="gp-axis-name">trigger {index}</span>
        <span className="gp-axis-val">{value.toFixed(4)}</span>
      </div>
      <div className="gp-axis-track">
        <div className="gp-axis-thumb" style={{ left: pos + "%" }} />
      </div>
    </div>
  );
}

function Axis({ index, value }) {
  // Map -1..1 onto 0..100 for the fill; center line sits at 50%.
  const pos = ((value + 1) / 2) * 100;
  return (
    <div className="gp-axis">
      <div className="gp-axis-row">
        <span className="gp-axis-name">axis {index}</span>
        <span className="gp-axis-val">{value.toFixed(4)}</span>
      </div>
      <div className="gp-axis-track">
        <div className="gp-axis-center" />
        <div className="gp-axis-thumb" style={{ left: pos + "%" }} />
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100%",
    background: "#0F1216",
    color: "#D7DDE4",
    fontFamily:
      "ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, monospace",
    padding: "28px 18px 60px",
    boxSizing: "border-box",
    border: "2px solid #232a33",
    margin: "1rem auto",
    borderRadius: 14,
  },
  wrap: { maxWidth: 860, margin: "0 auto" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    paddingBottom: 22,
    marginBottom: 26,
    borderBottom: "1px solid #232a33",
  },
  headMark: {
    fontSize: 26,
    color: "#5EE6A8",
    lineHeight: 1,
    filter: "drop-shadow(0 0 8px rgba(94,230,168,.55))",
  },
  title: {
    margin: 0,
    fontSize: 20,
    letterSpacing: "0.02em",
    fontWeight: 600,
    color: "#F2F5F8",
  },
  sub: { margin: "4px 0 0", fontSize: 12, color: "#6E7A88" },
  count: {
    marginLeft: "auto",
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    lineHeight: 1,
  },
  countNum: { fontSize: 22, fontWeight: 700, color: "#5EE6A8" },
  countLbl: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "#6E7A88",
    marginTop: 4,
  },
  empty: {
    textAlign: "center",
    padding: "70px 20px",
    border: "1px dashed #2a323d",
    borderRadius: 14,
  },
  emptyPulse: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "#5EE6A8",
    margin: "0 auto 18px",
    animation: "gp-pulse 1.6s ease-in-out infinite",
  },
  emptyTitle: { margin: 0, fontSize: 15, color: "#C3CBD4" },
  emptyHint: { margin: "8px 0 0", fontSize: 12.5, color: "#6E7A88" },
  card: {
    background: "#161B21",
    border: "1px solid #232a33",
    borderRadius: 14,
    padding: 20,
    marginBottom: 18,
  },
  cardHead: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: "1px solid #232a33",
    flexWrap: "wrap",
  },
  slot: {
    fontSize: 13,
    fontWeight: 700,
    color: "#5EE6A8",
    background: "rgba(94,230,168,.1)",
    padding: "2px 8px",
    borderRadius: 6,
  },
  gpId: {
    fontSize: 13.5,
    color: "#E4E9EE",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "60%",
  },
  mapping: {
    marginLeft: "auto",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "#6E7A88",
    border: "1px solid #2a323d",
    padding: "2px 7px",
    borderRadius: 5,
  },
  section: { marginTop: 18 },
  sectionLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    color: "#6E7A88",
    marginBottom: 12,
  },
  buttons: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(46px, 1fr))",
    gap: 8,
  },
  axes: { display: "flex", flexWrap: "wrap", gap: 40 },
};

const css = `
@keyframes gp-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .35; transform: scale(.75); }
}
.gp-btn {
  position: relative;
  height: 46px;
  border-radius: 8px;
  background: #1E252D;
  border: 1px solid #2a323d;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color .08s, box-shadow .08s;
}
.gp-btn-fill {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  background: linear-gradient(180deg, #2f6f52, #1f4d39);
  transition: height .05s linear;
}
.gp-btn-num {
  position: relative;
  font-size: 13px;
  font-weight: 600;
  color: #8b96a2;
}
.gp-btn.is-touched {
  border-color: #E8A33D;
  box-shadow: inset 0 0 0 1px rgba(232,163,61,.35);
}
.gp-btn.is-pressed {
  border-color: #5EE6A8;
  box-shadow: 0 0 0 1px #5EE6A8, 0 0 14px rgba(94,230,168,.4);
}
.gp-btn.is-pressed .gp-btn-fill {
  background: linear-gradient(180deg, #5EE6A8, #2f9c6e);
}
.gp-btn.is-pressed .gp-btn-num { color: #08110c; }
.gp-axis {
  flex: 1 1 100%;
}
.gp-axis-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 6px;
}
.gp-axis-name { color: #8b96a2; }
.gp-axis-val { color: #E4E9EE; font-variant-numeric: tabular-nums; }
.gp-axis-track {
  position: relative;
  height: 10px;
  background: #1E252D;
  border: 1px solid #2a323d;
  border-radius: 6px;
}
.gp-axis-center {
  position: absolute;
  left: 50%; top: 0; bottom: 0;
  width: 1px;
  background: #3a434f;
}
.gp-axis-thumb {
  position: absolute;
  top: 50%;
  width: 12px; height: 12px;
  border-radius: 50%;
  background: #5EE6A8;
  /* left is set inline as a %, positioned relative to the track; transform
     here only centers the dot on that point. No transition: the RAF poll
     loop already updates left every frame at display refresh rate, so a
     transition would only add lag by chasing a target that keeps moving. */
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px rgba(94,230,168,.5);
}
.gp-stick {
  flex: 0 0 150px;
  display: flex;
  flex-direction: column;
}
.gp-stick-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  margin-bottom: 6px;
}
.gp-stick-row .gp-axis-name,
.gp-stick-row .gp-axis-val {
  white-space: nowrap;
}
.gp-stick-circle {
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: #1E252D;
  border: 1px solid #2a323d;
  box-shadow: inset 0 0 20px rgba(0,0,0,.3);
}
.gp-stick-cross-h,
.gp-stick-cross-v {
  position: absolute;
  background: #2a323d;
}
.gp-stick-cross-h { left: 10%; right: 10%; top: 50%; height: 1px; }
.gp-stick-cross-v { top: 10%; bottom: 10%; left: 50%; width: 1px; }
.gp-stick-dot {
  position: absolute;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: #5EE6A8;
  /* left/top set inline as %, relative to the circle; transform only
     centers the dot on that point. No transition, same reasoning as
     .gp-axis-thumb. */
  transform: translate(-50%, -50%);
  box-shadow: 0 0 12px rgba(94,230,168,.55);
}
`;